import React from 'react';
import styled from 'styled-components';
import { mixColors } from '../../../utils';
import Button from '../../elements/Button';
import { H2, H3, H4, H5, P, PExtraSmall, PSmall } from '../../elements/Text';
import { Input } from '../../elements/Input';

export const ReadableContainer = styled.div`
  max-width: 1000px;
  width: 100%;
  margin: auto;
  height: 100%;

  @media only screen and (max-width: 600px) {
    padding: 0 16px;
  }
`;

export const SmallReadableSection = styled.div`
  max-width: 500px;
  width: 100%;
`;

export const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  flex-direction: column;
  gap: 32px;
  text-align: center;
  flex: 1;
  padding: 72px 32px;

  @media only screen and (max-width: 600px) {
    gap: 24px;
    padding: 72px 0;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

export const PillButton = styled(Button).attrs(({ theme }) => ({
  type: 'default',
  showIconBeforeLabel: true,
  backgroundColor: theme.colors.bgPage,
  color: theme.colors.textActive,
}))<{ isSelected: boolean | undefined }>`
  border-radius: 36px !important;
  text-align: center;
  border: ${({ isSelected, theme }) =>
    isSelected
      ? `1px solid ${theme.colors.primary} !important`
      : '1px solid rgba(161, 161, 170, 0.48)'};
  color: white;
  margin-top: 0;

  @media screen and (max-width: 600px) {
    width: 100%;
  }
  @media screen and (min-width: 768px) {
    &:hover {
      border: 1px solid ${({ theme }) => theme.colors.primary} !important;
      background-color: ${({ theme }) =>
        mixColors(theme.colors.bgPage, theme.colors.white, 0.075)} !important;
    }
  }
  @media screen and (max-width: 768px) {
    &:hover {
      background-color: transparent !important;
      ${({ isSelected }) =>
        isSelected
          ? `background-color: transparent !important;`
          : 'border: 1px solid rgba(161, 161, 170, 0.48) !important'}
    }
    &:active {
      border: 1px solid ${({ theme }) => theme.colors.primary} !important;
      background-color: ${({ theme }) =>
        mixColors(theme.colors.bgPage, theme.colors.white, 0.075)} !important;
    }
  }

  span {
    font-weight: 400 !important;
    white-space: normal;
    word-wrap: break-word;
  }
`;

export const MainPillButton = styled(Button).attrs(({ theme }) => ({
  type: 'secondary',
  showIconBeforeLabel: true,
  backgroundColor: theme.colors.bgPage,
  color: theme.colors.textActive,
}))<{ isSelected: boolean }>`
  min-width: 275px;
  justify-content: flex-start;
  border-radius: 100px;
  gap: 12px;
  border: 2px solid
    ${({ theme, isSelected }) =>
      isSelected ? theme.colors.primaryBorder : theme.colors.navigation};
  padding: 16px 28px;
  margin-top: 0;
  i {
    font-size: 26px;
  }
  span {
    font-size: 18px;
  }

  @media screen and (max-width: 600px) {
    width: 100%;
  }

  &:active,
  &:hover {
    border: 2px solid ${({ theme }) => theme.colors.primaryBorder} !important;
  }
`;

export const CTAButton = styled(Button).attrs(({ theme }) => ({
  type: 'primary',
  backgroundColor: theme.colors.bgPage,
}))`
  border-radius: 12px;
`;

export const BackgroundContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url('https://img.enkipro.com/424520efcd76fbaa5b804b844d676705.png');
  background-size: cover;
  background-position: center;
  width: 100%;
`;

export const SubHeader = styled(H3)`
  margin-top: 0;
  padding-bottom: 0;
  font-weight: 400;
  font-size: 27px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const FontAwesomeIcon = styled.i`
  color: ${({ theme }) => theme.colors.black};
`;
export const StarIcon = styled.i`
  color: ${({ theme }) => theme.colors.secondary};
`;

export const CheckIcon = styled.i`
  color: ${({ theme }) => theme.colors.primary};
  margin-right: 8px;
`;
const BarWrapper = styled.div`
  position: absolute;
  width: 100vw;
  top: 40px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;
const BarContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 88%;
`;
const StepCircle = styled.div`
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
export const CurrentStepCircle = styled(StepCircle)`
  border: ${({ theme }) => `2px solid ${theme.colors.primary}`};
  color: ${({ theme }) => theme.colors.textActive};

  font-size: 16px;
`;
const FutureStepCircle = styled(StepCircle)`
  border: 2px solid ${({ theme }) => theme.colors.textPlaceholder};
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 16px;
`;
export const CompletedStepCircle = styled(StepCircle)`
  background-color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
`;

const ProgressRect = styled.div<{ isCompleted: boolean }>`
  flex-grow: 1;
  height: 4px;
  background-color: ${({ theme, isCompleted }) =>
    isCompleted ? theme.colors.primary : theme.colors.textPlaceholder};
  margin: 0;
`;

export const ProgressBar = ({ currentStep }: { currentStep: number }) => {
  return (
    <BarWrapper>
      <BarContainer>
        {[1, 2, 3, 4, 5, 6].map((i) => {
          if (i === currentStep) {
            return (
              <React.Fragment key={`progress-bar-step-${i}`}>
                <CurrentStepCircle>{i}</CurrentStepCircle>
                {i < 6 && <ProgressRect isCompleted={false} />}
              </React.Fragment>
            );
          } else if (i < currentStep) {
            return (
              <React.Fragment key={`progress-bar-step-${i}`}>
                <CompletedStepCircle>
                  <FontAwesomeIcon className={'fa-duotone fa-check'} />
                </CompletedStepCircle>
                {i < 6 && <ProgressRect isCompleted={true} />}
              </React.Fragment>
            );
          } else {
            return (
              <React.Fragment key={`progress-bar-step-${i}`}>
                <FutureStepCircle>{i}</FutureStepCircle>{' '}
                {i < 6 && <ProgressRect isCompleted={false} />}
              </React.Fragment>
            );
          }
        })}
      </BarContainer>
    </BarWrapper>
  );
};
export const Header = styled(H2)`
  margin-top: 0;
  padding-bottom: 0;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media only screen and (max-width: 600px) {
    font-size: 24px;
    line-height: 1.2;
  }
`;

export const Label = styled(P)<{ textAlign?: 'right' | 'left' | 'center' }>`
  margin-top: 0;
  padding-bottom: 0;
  color: ${({ theme }) => theme.colors.textActive};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};

  @media only screen and (max-width: 600px) {
    font-size: 16px;
    line-height: 1.5;
  }
`;

export const LargeLabel = styled(H4)<{
  textAlign?: 'right' | 'left' | 'center';
}>`
  margin-top: 0;
  padding-bottom: 0;
  color: ${({ theme }) => theme.colors.textActive};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
`;

export const HighlightSpan = styled.span`
  color: ${({ theme }) => theme.colors.secondary};
`;

export function HighlightWord({ text, word }: { text: string, word: string }) {
  const [pre, post] = text.split(word);
  return (
    <>
      {pre.trim()}{' '}
      <HighlightSpan>{word}</HighlightSpan>
      {' '}{post.trim()}
    </>
  )
}

export const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

export const MainImage = styled.img`
  margin: 0;
  width: 285px;
  height: 285px;

  @media only screen and (max-width: 600px) {
    width: 200px;
    height: auto;
  }
`;

export const QuoteContainer = styled.div`
  width: 600px;
  padding: 0;
`;

const QuoteParagraph = styled(H5)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textActive};
  text-align: left;
  font-size: 22px;
  font-family: Georgia;
  padding: 4px 0;
  max-width: 700px;

  @media only screen and (max-width: 600px) {
    font-size: 18px;
  }
`;

const ProfileImage = styled.img`
  width: 40px;
  border-radius: 100%;
  margin: 0;
  padding: 0;
`;

const Column = styled.div<{
  gap?: number;
  centerAlignOnMobile?: boolean;
  textAlign?: 'left' | 'right' | 'center' | 'inherit';
}>`
  display: flex;
  flex-direction: column;
  ${({ gap }) => gap && `gap: ${gap}px`};
  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};
  @media only screen and (max-width: 600px) {
    ${({ centerAlignOnMobile }) =>
      centerAlignOnMobile &&
      `
      text-align: center;
      align-items: center;
    `};
  }
`;

const Row = styled.div<{ gap?: number; columnDirectionOnMobile?: boolean }>`
  display: flex;
  flex-direction: row;
  ${({ gap }) => gap && `gap: ${gap}px`};

  @media only screen and (max-width: 600px) {
    ${({ columnDirectionOnMobile }) =>
      columnDirectionOnMobile &&
      `
      flex-direction: column;
      align-items: center;
    `};
  }
`;

const QuoteIndicator = styled.div`
  width: 8px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.secondary};
`;

export const QuoteAuthor = styled(PSmall)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0;
  padding: 0;
`;

export const QuotePosition = styled(PExtraSmall)`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0;
  padding: 0;
`;

export const QuoteWithStars = ({
  text,
  author,
  profileImage,
}: {
  text: string;
  author: string;
  profileImage: string;
}) => {
  return (
    <Column gap={12}>
      <Row gap={12}>
        <QuoteIndicator />
        <QuoteParagraph> {text}</QuoteParagraph>
      </Row>
      <Row>
        {[0, 1, 2, 3, 4].map((i) => {
          return (
            <StarIcon className={`fa-solid fa-star`} key={`star-icon-${i}`} />
          );
        })}
      </Row>
      <Row gap={9} style={{ alignItems: 'center' }}>
        <ProfileImage src={profileImage} />
        <Column gap={4} style={{ alignItems: 'start' }}>
          <QuoteAuthor>{author}</QuoteAuthor>
        </Column>
      </Row>
    </Column>
  );
};
export const Quote = ({
  text,
  author,
  position,
  profileImage,
}: {
  text: string;
  author: string;
  position: string;
  profileImage: string;
}) => {
  return (
    <Column gap={12}>
      <Row gap={12}>
        <QuoteIndicator />
        <QuoteParagraph> {text}</QuoteParagraph>
      </Row>
      <Row gap={9} style={{ alignItems: 'center' }}>
        <ProfileImage src={profileImage} />
        <Column gap={4} style={{ alignItems: 'start' }}>
          <QuoteAuthor>{author}</QuoteAuthor>
          <QuotePosition>{position}</QuotePosition>
        </Column>
      </Row>
    </Column>
  );
};

export const StyledInput = styled(Input)`
  margin: 0;
  padding-bottom: 0;

  & > div:first-child {
    padding: 12px 16px;
    border-radius: 12px;
  }
`;

const AvatarImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 100%;
  margin: 0;
  padding: 0;
  aspect-ratio: 1;
`;

const QuoteText = styled(H4)`
  padding: 0;
  margin: 0;
  font-family: Georgia;
  color: ${({ theme }) => theme.colors.textActive};
  font-weight: normal;
  line-height: normal;
`;
export function LargeQuote({
  avatarURL,
  quote,
  author,
  position,
}: {
  avatarURL: string;
  quote: string;

  author: string;
  position: string;
}) {
  return (
    <Row style={{ gap: 32 }} columnDirectionOnMobile={true}>
      <AvatarImage src={avatarURL} />
      <Column style={{ gap: 12 }} centerAlignOnMobile={true} textAlign={'left'}>
        <QuoteText>"{quote}"</QuoteText>
        <Column style={{ gap: 4 }}>
          <QuoteAuthor>{author}</QuoteAuthor>
          <QuotePosition>{position}</QuotePosition>
        </Column>
      </Column>
    </Row>
  );
}

const ButtonEnabledInputWrapper = styled.div<{ isInputting: boolean }>`
  transition: width 0.5s ease-in-out;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  border-radius: 36px !important;
  text-align: center;

  cursor: pointer;
  border: ${({ isInputting, theme }) =>
    isInputting
      ? `1px solid ${theme.colors.primary} !important`
      : '1px solid rgba(161, 161, 170, 0.48)'};

  ${({ isInputting, theme }) =>
    isInputting
      ? `
  width: 350px;    
  padding: 10px 0px 14px 0px;
    background-color: ${theme.colors.inputBackground};
  `
      : `
  width: 125px;
  padding: 12px 16px;

  `}

  @media only screen and (max-width: 600px) {
    width: 100%;
  }

  span {
    font-weight: 400 !important;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.textActive};
  }
  input {
    border: none;
    background-color: transparent;
    margin: 0;
    font-weight: 400 !important;
    font-size: 16px;

    &:focus,
    &:active,
    &:hover {
      outline: none !important;
      border: none !important;
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.textDefault} !important;
    }
  }

  & > div {
    margin: 0;
    padding: 0;
    height: 25px;
    border-radius: 36px !important;
    border: none;
  }
  & > div > div {
    border-radius: 36px !important;
    border-width: 0px !important;
    padding-top: 0px;
    padding-bottom: 0px;
  }
`;

const WriteIcon = styled.i`
  color: ${({ theme }) => theme.colors.textActive};
  margin-left: 4px;
`;

export function ButtonEnabledInput({
  isInputting,
  setIsInputting,
  inputValue,
  setInputValue,
}: {
  isInputting: boolean;
  setIsInputting: (value: boolean) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
}) {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onButtonPress = () => {
    setIsInputting(true);
    setInputValue('');
  };

  return (
    <ButtonEnabledInputWrapper isInputting={isInputting}>
      {isInputting ? (
        <Input
          placeholder="Other"
          value={inputValue}
          onChange={handleInputChange}
        />
      ) : (
        <span onClick={onButtonPress}>
          Other <WriteIcon className="fas fa-pen" />
        </span>
      )}
    </ButtonEnabledInputWrapper>
  );
}
